/**
 * G1: 風船膨らまし（BART 系）
 * ゲームロジックと DOM 描画を担当する。
 * スコア計算は src/scoring/balloon.ts に委譲する。
 */

import { computeBalloonScore, type BalloonTrialRecord } from '../scoring/balloon.js';

const TOTAL_TRIALS = 30;
// 破裂閾値: 1〜128 の整数一様分布（Lejuez 2002 BART 標準パラメタ）
const BURST_MIN = 1;
const BURST_MAX = 128;

function randInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

interface BalloonState {
  trial: number;
  pumps: number;
  burstThreshold: number;
  phase: 'pumping' | 'result';
  records: BalloonTrialRecord[];
  totalBanked: number;
}

/** 風船のSVG半径: ポンプ数に応じて拡大 (基本 40px、最大 100px) */
function calcRadius(pumps: number): number {
  const base = 40;
  const max = 100;
  return Math.min(base + pumps * 2, max);
}

/** 風船の色: 膨らむにつれ赤みが強くなる */
function calcColor(pumps: number): string {
  // 0〜128 pump で hue 220（青）→ 0（赤）
  const hue = Math.max(0, 220 - Math.round(pumps * 1.72));
  return `hsl(${hue}, 80%, 60%)`;
}

function renderBalloonSvg(pumps: number, burst: boolean): string {
  if (burst) {
    // 破裂アニメーション: × 記号
    return `
      <svg width="120" height="120" viewBox="0 0 120 120">
        <text x="50%" y="55%" text-anchor="middle" dominant-baseline="middle"
          font-size="72" fill="#ff6b6b">💥</text>
      </svg>`;
  }
  const r = calcRadius(pumps);
  const color = calcColor(pumps);
  const cx = 60;
  const cy = 120 - r - 10;
  const stringY = cy + r;
  return `
    <svg width="120" height="240" viewBox="0 0 120 240">
      <!-- 風船本体 -->
      <ellipse cx="${cx}" cy="${cy}" rx="${r}" ry="${r * 1.1}" fill="${color}" opacity="0.9"/>
      <!-- ハイライト -->
      <ellipse cx="${cx - r * 0.25}" cy="${cy - r * 0.25}" rx="${r * 0.2}" ry="${r * 0.15}"
        fill="white" opacity="0.5"/>
      <!-- 紐 -->
      <line x1="${cx}" y1="${stringY}" x2="${cx}" y2="230"
        stroke="#aaa" stroke-width="2" stroke-linecap="round"/>
    </svg>`;
}

export function startBalloonGame(container: HTMLElement, onComplete: (records: BalloonTrialRecord[]) => void): void {
  const state: BalloonState = {
    trial: 0,
    pumps: 0,
    burstThreshold: randInt(BURST_MIN, BURST_MAX),
    phase: 'pumping',
    records: [],
    totalBanked: 0,
  };

  function render(): void {
    const progressPct = (state.trial / TOTAL_TRIALS) * 100;

    container.innerHTML = `
      <div class="trial-counter">試行 ${state.trial + 1} / ${TOTAL_TRIALS}</div>
      <div class="progress-bar-wrap">
        <div class="progress-bar-fill" style="width: ${progressPct}%"></div>
      </div>
      <div class="balloon-arena">
        <div class="balloon-svg-wrap" id="balloon-svg">
          ${renderBalloonSvg(state.pumps, false)}
        </div>
        <div class="balloon-points">${state.pumps}</div>
        <div class="balloon-points-label">現在のポイント（保管で確定）</div>
        <div class="balloon-message" id="balloon-msg"></div>
        <div class="balloon-actions">
          <button class="btn btn-primary" id="btn-pump">ポンプ ＋1</button>
          <button class="btn btn-secondary" id="btn-bank">保管して次へ</button>
        </div>
      </div>
      <div style="text-align:center; font-size:0.8rem; color:#bbb; margin-top:16px;">
        合計確定: ${state.totalBanked} pt
      </div>
    `;

    document.getElementById('btn-pump')!.addEventListener('click', handlePump);
    document.getElementById('btn-bank')!.addEventListener('click', handleBank);
  }

  function handlePump(): void {
    if (state.phase !== 'pumping') return;
    state.pumps += 1;

    // 破裂判定
    if (state.pumps >= state.burstThreshold) {
      handleBurst();
      return;
    }

    // 風船SVGだけ更新
    const svgWrap = document.getElementById('balloon-svg');
    if (svgWrap) svgWrap.innerHTML = renderBalloonSvg(state.pumps, false);

    const pts = container.querySelector('.balloon-points');
    if (pts) pts.textContent = String(state.pumps);
  }

  function handleBurst(): void {
    state.phase = 'result';

    // 破裂アニメーション
    const svgWrap = document.getElementById('balloon-svg');
    if (svgWrap) svgWrap.innerHTML = renderBalloonSvg(0, true);

    const msg = document.getElementById('balloon-msg')!;
    msg.textContent = '💥 破裂！ポイントが消えました';
    msg.className = 'balloon-message burst';

    const pumpBtn = document.getElementById('btn-pump') as HTMLButtonElement;
    const bankBtn = document.getElementById('btn-bank') as HTMLButtonElement;
    pumpBtn.disabled = true;
    bankBtn.disabled = true;

    state.records.push({ pumps: state.pumps, burst: true, banked: 0 });

    setTimeout(() => advanceTrial(), 1400);
  }

  function handleBank(): void {
    if (state.phase !== 'pumping' || state.pumps === 0) return;
    state.phase = 'result';

    const banked = state.pumps;
    state.totalBanked += banked;
    state.records.push({ pumps: banked, burst: false, banked });

    const msg = document.getElementById('balloon-msg')!;
    msg.textContent = `✅ ${banked} pt 確定！`;
    msg.className = 'balloon-message saved';

    const pumpBtn = document.getElementById('btn-pump') as HTMLButtonElement;
    const bankBtn = document.getElementById('btn-bank') as HTMLButtonElement;
    pumpBtn.disabled = true;
    bankBtn.disabled = true;

    setTimeout(() => advanceTrial(), 900);
  }

  function advanceTrial(): void {
    state.trial += 1;
    if (state.trial >= TOTAL_TRIALS) {
      onComplete(state.records);
      return;
    }
    // 次の試行を準備
    state.pumps = 0;
    state.burstThreshold = randInt(BURST_MIN, BURST_MAX);
    state.phase = 'pumping';
    render();
  }

  render();
}

export { computeBalloonScore };
